import React, { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { isSetupCompleted, getSetupInProgressKey, isSetupNotStarted, isSetupAllowedForUser } from 'utils/setupStorage'
import keys from 'constants/keys'

export const SetupGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const uid = useSelector((state: RootState) => state.auth.uid)
  const isOnSetupPage = location.pathname === keys.ROUTE_NAMES.SETUP

  useEffect(() => {
    if (!uid) return

    // Check if user is allowed to access setup feature
    if (!isSetupAllowedForUser(uid)) {
      // If user is on /setup page and not allowed, redirect to home
      if (isOnSetupPage) {
        navigate(keys.ROUTE_NAMES.NEWS_FEED)
      }

      return
    }

    // Skip login pages
    if (location.pathname.includes('login') || location.pathname.includes('forgot-password')) {
      return
    }

    const setupComplete = isSetupCompleted(uid)
    const setupNotStarted = isSetupNotStarted(uid)

    // If setup completed — forbid entering /setup and redirect
    if (setupComplete && isOnSetupPage) {
      navigate(keys.ROUTE_NAMES.NEWS_FEED)
      return
    }

    // Allow navigation during setup in progress for this specific user
    if (localStorage.getItem(getSetupInProgressKey(uid))) {
      return
    }

    // Don't redirect if already on /setup
    if (location.pathname === keys.ROUTE_NAMES.SETUP) return

    // Check if user manually clicked on navigation (has fromUserClick in state)
    const isUserClick = location.state && (location.state as any).fromUserClick === true

    // Redirect to /setup if setup not started AND user didn't manually click
    // This allows:
    // - Redirect on page load/refresh (no user click)
    // - Allow navigation when user clicks on navbar/sidebar
    if (setupNotStarted && !isUserClick) {
      navigate(keys.ROUTE_NAMES.SETUP)
    }
  }, [uid, location.pathname, location.state, navigate, isOnSetupPage])

  return <>{children}</>
}
